import { readonly, ref } from 'vue';
import { profileService } from '../services/profileService.js';
import { usersService } from '../services/usersService.js';
import { clearJournalResponses } from '../utils/journalResponseCache.js';

const USER_CONTEXT_STORAGE_KEY = 'corecry:user-context';
const USER_AVATAR_STORAGE_KEY = 'corecry:user-avatar';

const userContext = ref(null);
const userAvatar = ref('');
let inFlight = null;
let contextRevision = 0;
let avatarInFlight = null;

const getSessionStorage = () => {
    if (typeof window === 'undefined') return null;
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
};

const readCachedValue = (key) => {
    const storage = getSessionStorage();
    if (!storage) return null;
    try {
        return JSON.parse(storage.getItem(key));
    } catch {
        return null;
    }
};

const writeCachedValue = (key, value) => {
    const storage = getSessionStorage();
    if (!storage) return;
    try {
        storage.setItem(key, JSON.stringify(value));
    } catch {
        // Session storage is an optional rendering cache.
    }
};

const cacheableUserContext = (data) => ({
    user_id: data.user_id || '',
    username: data.username || '',
    registered_at: data.registered_at || '',
    is_owner: Boolean(data.is_owner),
    switched_by_admin: Boolean(data.switched_by_admin)
});

const cachedContext = readCachedValue(USER_CONTEXT_STORAGE_KEY);
if (cachedContext?.user_id) {
    userContext.value = cacheableUserContext(cachedContext);
}
const cachedAvatar = readCachedValue(USER_AVATAR_STORAGE_KEY);
if (typeof cachedAvatar === 'string') {
    userAvatar.value = cachedAvatar;
}

export const buildUserContextKey = (user) => {
    if (!user?.user_id) return null;
    const impersonation = user.switched_by_admin ? 'impersonated' : 'direct';
    return `${String(user.user_id)}:${impersonation}`;
};

export const loadUserContext = async ({ force = false } = {}) => {
    if (!force && userContext.value) return userContext.value;
    if (inFlight) return inFlight;

    const revision = contextRevision;
    const request = usersService.info({ credentials: 'include' })
        .then((data) => {
            if (revision !== contextRevision) throw new DOMException('User context changed', 'AbortError');
            if (buildUserContextKey(userContext.value) !== buildUserContextKey(data)) clearJournalResponses(buildUserContextKey(userContext.value));
            userContext.value = cacheableUserContext(data);
            writeCachedValue(USER_CONTEXT_STORAGE_KEY, userContext.value);
            return data;
        })
        .finally(() => {
            if (inFlight === request) inFlight = null;
        });
    inFlight = request;
    return inFlight;
};

export const setUserAvatar = (avatar) => {
    userAvatar.value = avatar || '';
    writeCachedValue(USER_AVATAR_STORAGE_KEY, userAvatar.value);
};

export const loadUserAvatar = async ({ force = false } = {}) => {
    if (!force && typeof userAvatar.value === 'string' && userAvatar.value) {
        return userAvatar.value;
    }
    if (avatarInFlight) return avatarInFlight;

    avatarInFlight = profileService.avatar({ credentials: 'include' })
        .then((data) => {
            setUserAvatar(data.avatar || '');
            return userAvatar.value;
        })
        .finally(() => {
            avatarInFlight = null;
        });

    return avatarInFlight;
};

export const clearUserContext = () => {
    contextRevision++;
    clearJournalResponses();
    userContext.value = null;
    userAvatar.value = '';
    inFlight = null;
    avatarInFlight = null;
    const storage = getSessionStorage();
    if (storage) {
        storage.removeItem(USER_CONTEXT_STORAGE_KEY);
        storage.removeItem(USER_AVATAR_STORAGE_KEY);
    }
};

export function useUserContext() {
    return {
        userContext: readonly(userContext),
        userAvatar: readonly(userAvatar),
        loadUserContext,
        loadUserAvatar,
        setUserAvatar,
        clearUserContext,
        buildUserContextKey
    };
}
